import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { 
  FiGrid, 
  FiList, 
  FiSearch, 
  FiX, 
  FiPlus, 
  FiArrowUp, 
  FiArrowDown,
  FiSliders
} from 'react-icons/fi';

type SortKey = 'name' | 'created' | 'updated' | 'lastExecuted' | 'successRate';

interface WorkflowCommandBarProps {
  // View mode
  viewMode: 'grid' | 'table';
  onViewModeChange: (mode: 'grid' | 'table') => void;
  
  // Sort
  sortKey: SortKey;
  onSortKeyChange: (key: SortKey) => void;
  sortDir: 'asc' | 'desc';
  onToggleSortDir: () => void;
  
  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;
  
  // Stats
  resultCount: number;
  totalCount: number;
  
  // Actions
  onCreateClick: () => void;
  isLoading?: boolean;
}

const sortOptions: { key: SortKey; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { key: 'name', label: 'Name', defaultDir: 'asc' },
  { key: 'created', label: 'Created Date', defaultDir: 'desc' },
  { key: 'updated', label: 'Last Modified', defaultDir: 'desc' },
  { key: 'lastExecuted', label: 'Last Executed', defaultDir: 'desc' },
  { key: 'successRate', label: 'Success Rate', defaultDir: 'desc' },
];

export const WorkflowCommandBar: React.FC<WorkflowCommandBarProps> = ({
  viewMode,
  onViewModeChange,
  sortKey,
  onSortKeyChange,
  sortDir,
  onToggleSortDir,
  searchQuery,
  onSearchChange,
  resultCount,
  totalCount,
  onCreateClick,
  isLoading
}) => {
  const { darkMode } = useTheme();
  
  // Tinted glass background - blue/indigo tint distinct from chat's green
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#3b82f6' : '#6366f1',
    enabled: true
  });
  
  const currentSortOption = sortOptions.find(opt => opt.key === sortKey);
  
  return (
    <div
      ref={tintedBg.ref}
      className={`
        relative rounded-xl border shadow-lg mb-6 overflow-hidden
        ${darkMode 
          ? 'border-gray-700/50 shadow-blue-500/10' 
          : 'border-gray-200/50 shadow-blue-500/10'
        }
      `}
      style={tintedBg.style}
    >
      {/* Animated tint overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={tintedBg.overlayStyle}
      />
      
      {/* WORKFLOWS Banner */}
      <div className={`
        relative z-10 w-full py-1.5 px-4 border-b
        ${darkMode 
          ? 'bg-gradient-to-r from-blue-900/80 via-indigo-900/80 to-blue-900/80 border-gray-700/50' 
          : 'bg-gradient-to-r from-blue-600/90 via-indigo-600/90 to-blue-600/90 border-gray-200/50'
        }
      `}>
        <div className="flex items-center justify-center">
          <div className={`
            text-xs font-black tracking-[0.3em] uppercase
            ${darkMode ? 'text-blue-200' : 'text-white'}
            drop-shadow-sm
          `}>
            WORKFLOWS
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className={`
        relative z-10 px-4 py-3 flex flex-col md:flex-row md:items-center gap-4
        ${darkMode 
          ? 'bg-gradient-to-r from-gray-900/90 via-gray-800/90 to-gray-900/90' 
          : 'bg-gradient-to-r from-white/90 via-gray-50/90 to-white/90'
        }
        backdrop-blur-md
      `}>
        {/* Left section: Search */}
        <div className="flex-1 min-w-0">
          <div className={`
            relative flex items-center
            ${darkMode ? 'text-gray-300' : 'text-gray-600'}
          `}>
            <FiSearch className="absolute left-3 w-4 h-4 opacity-50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Filter workflows..."
              className={`
                w-full pl-10 pr-10 py-2 rounded-lg text-sm transition-all
                border focus:outline-none focus:ring-2
                ${darkMode 
                  ? 'bg-gray-800/80 border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500' 
                  : 'bg-white/80 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-500'
                }
              `}
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className={`
                  absolute right-3 p-0.5 rounded-full transition-colors
                  ${darkMode 
                    ? 'hover:bg-gray-700 text-gray-400' 
                    : 'hover:bg-gray-200 text-gray-500'
                  }
                `}
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        
        {/* Center section: View Toggle & Sort */}
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className={`
            flex items-center p-1 rounded-lg border
            ${darkMode 
              ? 'bg-gray-800/50 border-gray-700' 
              : 'bg-white/50 border-gray-200'
            }
          `}>
            <button
              onClick={() => onViewModeChange('grid')}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${viewMode === 'grid'
                  ? darkMode
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'bg-blue-500 text-white shadow-inner'
                  : darkMode
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
              title="Grid view"
            >
              <FiGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => onViewModeChange('table')}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${viewMode === 'table'
                  ? darkMode
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'bg-blue-500 text-white shadow-inner'
                  : darkMode
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
              title="Table view"
            >
              <FiList className="w-4 h-4" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
          
          {/* Sort Control */}
          <div className="flex items-center gap-2">
            <div className={`
              relative flex items-center
              ${darkMode ? 'text-gray-300' : 'text-gray-600'}
            `}>
              <FiSliders className="absolute left-2 w-3.5 h-3.5 opacity-50" />
              <select
                value={sortKey}
                onChange={(e) => onSortKeyChange(e.target.value as SortKey)}
                className={`
                  pl-7 pr-8 py-1.5 rounded-lg text-sm appearance-none cursor-pointer
                  border focus:outline-none focus:ring-2
                  ${darkMode 
                    ? 'bg-gray-800/80 border-gray-600 text-gray-100 focus:ring-blue-500/50 focus:border-blue-500' 
                    : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-blue-500/30 focus:border-blue-500'
                  }
                `}
              >
                {sortOptions.map(option => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Sort Direction Toggle */}
            <button
              onClick={onToggleSortDir}
              className={`
                p-1.5 rounded-lg border transition-all
                ${darkMode 
                  ? 'bg-gray-800/80 border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'bg-white/80 border-gray-300 text-gray-600 hover:bg-gray-100'
                }
              `}
              title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortDir === 'asc' ? (
                <FiArrowUp className="w-4 h-4" />
              ) : (
                <FiArrowDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
        
        {/* Right section: Stats & Create */}
        <div className="flex items-center gap-4">
          {/* Result count */}
          <span className={`
            text-sm whitespace-nowrap
            ${darkMode ? 'text-gray-400' : 'text-gray-500'}
          `}>
            {resultCount} of {totalCount}
          </span>
          
          {/* Create Button */}
          <button
            onClick={onCreateClick}
            disabled={isLoading}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
              transition-all duration-200 shadow-lg
              ${isLoading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-xl hover:-translate-y-0.5'
              }
              bg-gradient-to-r from-blue-600 to-indigo-600 text-white
              hover:from-blue-500 hover:to-indigo-500
              shadow-blue-500/25
            `}
          >
            <FiPlus className="w-4 h-4" />
            <span>Create Workflow</span>
          </button>
        </div>
      </div>
    </div>
  );
};
